import React, { useEffect, useRef } from 'react';

import dayjs from '~/timeInit';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { HeatmapSurchargeCalendar, HeatmapSurchargeQuote } from '~/types/heatmapSurcharge';

import currencyFormatter from '~/utils/currencyFormatter';

import { formatCount } from './utils';

interface Props {
  calendar: HeatmapSurchargeCalendar;
  selectedDate?: string;
  onSelectDate: (date: string) => void;
  scrollRequest?: { date: string; sequence: number };
  selectableDates: ReadonlySet<string>;
  selectedDates: ReadonlySet<string>;
  onToggleSelectedDate: (date: string) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onReviewSelected: () => void;
  renderAction?: (quote: HeatmapSurchargeQuote) => React.ReactNode;
}

export default function HeatmapRecommendations({
  calendar,
  selectedDate,
  onSelectDate,
  scrollRequest,
  selectableDates,
  selectedDates,
  onToggleSelectedDate,
  onSelectAll,
  onClearSelection,
  onReviewSelected,
  renderAction,
}: Props) {
  const selectedRowRef = useRef<HTMLTableRowElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!scrollRequest || scrollRequest.date !== selectedDate) return;
    const selectedRow = selectedRowRef.current;
    const tableContainer = tableContainerRef.current;
    if (!selectedRow || !tableContainer) return;
    const rowBounds = selectedRow.getBoundingClientRect();
    const containerBounds = tableContainer.getBoundingClientRect();
    tableContainer.scrollTo({
      top:
        tableContainer.scrollTop +
        rowBounds.top -
        containerBounds.top -
        (tableContainer.clientHeight - rowBounds.height) / 2,
      behavior: 'smooth',
    });
    selectedRow.focus({ preventScroll: true });
  }, [scrollRequest, selectedDate]);

  return (
    <Paper
      variant="outlined"
      sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: { lg: '70vh' } }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} gap={1} p={2}>
        <Stack direction="row" alignItems="center" spacing={1} flexGrow={1}>
          <Typography variant="h6">Recommended dates</Typography>
          <Chip size="small" label={calendar.recommendations.length} />
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <Button size="small" disabled={!selectableDates.size} onClick={onSelectAll}>
            Select all ready
          </Button>
          <Button size="small" disabled={!selectedDates.size} onClick={onClearSelection}>
            Clear
          </Button>
          <Button variant="contained" size="small" disabled={!selectedDates.size} onClick={onReviewSelected}>
            Review selected ({selectedDates.size})
          </Button>
        </Stack>
      </Stack>
      {!calendar.recommendations.length ? (
        <Alert severity="info" sx={{ mx: 2, mb: 2 }}>
          No dates meet the surcharge rules for this room type and month. The full forecast is still shown in the
          calendar.
        </Alert>
      ) : (
        <TableContainer ref={tableContainerRef} sx={{ minHeight: { lg: 0 }, overflowY: { lg: 'auto' } }}>
          <Table size="small" aria-label="Heatmap surcharge recommendations">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={selectableDates.size > 0 && selectedDates.size === selectableDates.size}
                    indeterminate={selectedDates.size > 0 && selectedDates.size < selectableDates.size}
                    disabled={!selectableDates.size}
                    onChange={(_, checked) => (checked ? onSelectAll() : onClearSelection())}
                    inputProps={{ 'aria-label': 'Select all ready heatmap surcharges' }}
                  />
                </TableCell>
                <TableCell>Check-in date</TableCell>
                <TableCell>Statistics</TableCell>
                <TableCell>Suggested surcharge</TableCell>
                <TableCell>New nightly price</TableCell>
                {renderAction && <TableCell>Action</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {calendar.recommendations.map((recommendation) => {
                const date = recommendation.travelDate;
                const roomNight = calendar.roomNights.find((night) => night.travelDate === date);
                const price = calendar.pricingReferences.find((reference) => reference.travelDate === date);
                const quote = calendar.surchargeQuotes.find((candidate) => candidate.travelDate === date);
                const isViewing = date === selectedDate;
                const isBulkSelected = selectedDates.has(date);
                const isSelectable = selectableDates.has(date);
                return (
                  <TableRow
                    key={date}
                    ref={isViewing ? selectedRowRef : undefined}
                    selected={isViewing}
                    tabIndex={isViewing ? -1 : undefined}
                    aria-current={isViewing ? 'date' : undefined}
                    onClick={() => onSelectDate(date)}
                    hover
                    sx={{
                      cursor: 'pointer',
                      bgcolor: isBulkSelected ? (theme) => alpha(theme.palette.primary.main, 0.08) : undefined,
                      scrollMarginBlock: 12,
                      '&.Mui-selected': {
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
                      },
                      '&.Mui-selected:hover': {
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.22),
                      },
                      '&:focus-visible': {
                        outline: '3px solid',
                        outlineColor: 'primary.main',
                        outlineOffset: -3,
                      },
                    }}
                  >
                    <TableCell padding="checkbox" sx={{ verticalAlign: 'top', pt: 1.5 }}>
                      <Checkbox
                        size="small"
                        checked={isBulkSelected}
                        disabled={!isSelectable}
                        onChange={() => onToggleSelectedDate(date)}
                        inputProps={{ 'aria-label': `Select ${dayjs(date).format('D MMM YYYY')} surcharge` }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        verticalAlign: 'top',
                        borderLeft: 4,
                        borderLeftColor: isViewing ? 'primary.main' : 'transparent',
                      }}
                    >
                      <Button
                        variant="text"
                        onClick={() => onSelectDate(date)}
                        aria-pressed={isViewing}
                        sx={{ textAlign: 'left', whiteSpace: 'nowrap', px: 0 }}
                      >
                        {dayjs(date).format('DD/MM/YYYY')}
                        <br />
                        {dayjs(date).format('(ddd)')}
                      </Button>
                      {isViewing && <Chip size="small" color="primary" label="Viewing" sx={{ display: 'flex' }} />}
                    </TableCell>
                    <TableCell sx={{ minWidth: 225 }}>
                      <Stack spacing={0.5} py={1}>
                        <Typography variant="body2">
                          Predicted booked / allocated:{' '}
                          <strong>
                            {formatCount(recommendation.predictedFinalBookings)} /{' '}
                            {formatCount(roomNight?.allocatedAtGeneration ?? null)}
                          </strong>
                        </Typography>
                        <Typography variant="body2">
                          Booked at forecast: {formatCount(roomNight?.bookedAtGeneration ?? null)}
                        </Typography>
                        <Typography variant="body2">
                          Predicted rooms left: <strong>{formatCount(recommendation.predictedRoomsLeft)}</strong>
                        </Typography>
                        {price && (
                          <Box>
                            <Typography variant="body2">
                              Vendor surcharge: {currencyFormatter('AUD', price.vendorSurchargeTotal, 2)}
                            </Typography>
                            <Typography variant="body2">
                              Internal surcharge: {currencyFormatter('AUD', price.internalSurchargeTotal, 2)}
                            </Typography>
                            <Typography variant="body2">
                              Base nightly price: {currencyFormatter('AUD', price.baseNightlyPrice, 2)}
                            </Typography>
                            <Typography variant="body2">
                              Current nightly price: {currencyFormatter('AUD', price.currentNightlyPrice, 2)}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', pt: 2 }}>
                      <Stack spacing={1} alignItems="flex-start">
                        <Chip
                          size="small"
                          color="primary"
                          variant="outlined"
                          label={`${recommendation.suggestedSurchargePercentage}% of base`}
                        />
                        {quote ? (
                          <>
                            <Typography fontWeight="bold">
                              +{currencyFormatter('AUD', quote.additionalInternalSurcharge, 2)}
                            </Typography>
                            <Typography variant="caption">
                              Target internal surcharge: {currencyFormatter('AUD', quote.targetInternalSurcharge, 2)}
                            </Typography>
                            {quote.status === 'already_meets_target' && (
                              <Typography variant="body2" color="text.secondary">
                                Already meets target. No increase needed.
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {calendar.pricingContext.status === 'ready'
                              ? 'No currently sellable rate'
                              : 'Pricing unavailable'}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', pt: 2 }}>
                      <Typography fontWeight="bold">
                        {quote ? currencyFormatter('AUD', quote.proposedNightlyPrice, 2) : '—'}
                      </Typography>
                    </TableCell>
                    {renderAction && (
                      <TableCell sx={{ verticalAlign: 'top', pt: 2 }}>{quote ? renderAction(quote) : '—'}</TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Typography variant="caption" color="text.secondary" display="block" p={2}>
        Prices are in AUD and use the lowest sellable rate as a reference. Booking counts are from the forecast
        generation.
      </Typography>
    </Paper>
  );
}
